import { usePoll } from '@/hooks/live';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePhone } from '@/store/phone';

export interface AgentMe {
  agent: { id: number; code: string; name: string; state: string; state_reason: string | null; sip_registered: boolean; current_call_uuid: string | null; preview_contact_id: number | null };
  sip: { username: string; password: string; domain: string; uri: string; wss_url: string; ice_servers: RTCIceServer[] };
  campaigns: { id: number; name: string; dial_method: string; status: string }[];
  queues: { id: number; name: string; status: string }[];
}

// enabled: false for callers that might not have an agent_id (e.g. an admin without an extension)
export function useAgentMe(enabled = true) {
  return useQuery({ queryKey: ['agent-me'], queryFn: () => api<AgentMe>('/agent/me'), staleTime: 60_000, enabled });
}

export interface AgentStateReply { agent: { id: number; code: string; state: string; version: number; reason: string | null; call_uuid: string | null; registered: boolean; since: string } }

export function useAgentState(enabled = true) {
  const poll = usePoll(3000);
  return useQuery({ queryKey: ['agent-state'], queryFn: () => api<AgentStateReply>('/agent/state'), refetchInterval: poll, enabled });
}

// Portal login / logout: drop every SIP registration of the agent's extension and register again.
//   1. the softphone sends REGISTER "Contact: *" (all bindings, on the node it is connected to)
//   2. POST /api/agent/sip/reset: the engine flushes them on its node and marks the agent unregistered
//   3. a new REGISTER; then wait until the engine has seen it (registered again), so a login right
//      after is not hit by the flush (losing the registration while logged in = DISCONNECTED)
// register = false (sign out): steps 1 and 2 only. Returns true when registered again.
export async function freshRegistration(sip: AgentMe['sip'] | undefined, register = true): Promise<boolean> {
  const phone = usePhone.getState();
  await phone.unregisterAll();
  await api('/agent/sip/reset', { body: {} }).catch(() => {});
  if (!register) return false;
  let ok: boolean;
  if (phone.reg === 'disconnected' && sip) { await phone.connect(sip); ok = usePhone.getState().reg !== 'failed'; }
  else ok = await phone.reregister();
  if (!ok) return false;
  for (let i = 0; i < 20; i++) {
    const st = await api<AgentStateReply>('/agent/state').catch(() => null);
    if (st?.agent.registered) return true;
    await new Promise((r) => setTimeout(r, 250));
  }
  return false;
}
